import React, { FC, useState, useEffect } from 'react';
import classNames from 'classnames';
import { env } from 'Shared/constants/env';
import { Column, FormInput, Row } from 'Shared/components';
import { useFormikContext } from 'formik';
import { AddVehicleFormValues } from './VehicleForm';
import styles from './styles.module.css';
import {
  AddressType,
  VehicleType,
  VehicleOptionsQuestionsConfig,
} from '@clearcover/core-app-types';
import { combineStrings, formatAddress } from 'Shared/utils';
import { areSameAddress } from 'Policies/utils/helpers';

type Options = {
  [key: string]: string;
};

type VehicleAddressType = {
  mailingAddress: AddressType;
  parkingLocationReasons: Options;
  parkingTypes: Options;
  inStateParkingDurations: Options;
  vehicle?: VehicleType;
  vehicleOptionsQuestionsConfig: VehicleOptionsQuestionsConfig;
};

export const VehicleAddress: FC<VehicleAddressType> = ({
  mailingAddress,
  parkingLocationReasons,
  parkingTypes,
  inStateParkingDurations,
  vehicle,
  vehicleOptionsQuestionsConfig,
}) => {
  const {
    handleBlur,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormikContext<AddVehicleFormValues>();
  const [isParkedAtMailingAddress, setIsParkedAtMailingAddress] = useState(
    areSameAddress(mailingAddress, values.garagingAddress)
  );

  useEffect(() => {
    setFieldValue('parkedAtMailing', isParkedAtMailingAddress);
  }, [isParkedAtMailingAddress, setFieldValue]);

  return (
    <div
      data-testid={'garaging-address-form'}
      className={classNames('fs-exclude', styles.section)}
    >
      <h1>Garaging Address</h1>
      <Row>
        <Column>
          <FormInput
            variant={'radioButtonGroup'}
            label={`Is this vehicle parked at ${formatAddress(
              mailingAddress
            )}?`}
            tooltip="Select ‘Yes’ if the vehicle is parked on the street near the address listed."
            name={'parkedAtMailing'}
            options={{
              true: 'Yes',
              false: 'No',
            }}
            value={isParkedAtMailingAddress ? 'true' : 'false'}
            onChange={value => {
              if (value === 'true') {
                setFieldValue(
                  'garagingAddress.address1',
                  mailingAddress.address1
                );
                setFieldValue(
                  'garagingAddress.address2',
                  mailingAddress.address2
                );
                setFieldValue('garagingAddress.city', mailingAddress.city);
                setFieldValue('garagingAddress.state', mailingAddress.state);
                setFieldValue('garagingAddress.zip', mailingAddress.zip);
              } else {
                setFieldValue('garagingAddress.address1', '');
                setFieldValue('garagingAddress.address2', '');
                setFieldValue('garagingAddress.city', '');
                setFieldValue('garagingAddress.zip', '');
              }
              setFieldValue('parkingLocationReason', null);
              setIsParkedAtMailingAddress(
                prevParkedAtMailingAddress => !prevParkedAtMailingAddress
              );
            }}
          />
        </Column>
        <Column>
          {!isParkedAtMailingAddress &&
            vehicleOptionsQuestionsConfig.askForParkingLocationReason &&
            env.vehicleNewQuestionsEnabled && (
              <FormInput
                variant="selectAutocomplete"
                name="parkingLocationReason"
                data-testid="parking-location-reason"
                autoComplete={'off'}
                onChange={value => {
                  setFieldValue('parkingLocationReason', value);
                }}
                onBlur={handleBlur}
                value={values.parkingLocationReason}
                errorMessage={
                  touched.parkingLocationReason
                    ? errors.parkingLocationReason
                    : undefined
                }
                label="For what reason is this vehicle garaged at a separate location?"
                options={parkingLocationReasons}
                disabled={isParkedAtMailingAddress}
              />
            )}
        </Column>
      </Row>

      {!isParkedAtMailingAddress && (
        <FormInput
          variant="addressAutocomplete"
          prefix="garagingAddress"
          allowStateChange={false}
          values={{
            address1: values.garagingAddress.address1,
            address2: values.garagingAddress.address2,
            city: values.garagingAddress.city,
            state: values.garagingAddress.state,
            zip: values.garagingAddress.zip,
          }}
          onAddressSelect={selectedOption => {
            const { city, streetLine } = selectedOption;
            setFieldValue('garagingAddress.address1', streetLine);
            setFieldValue('garagingAddress.city', city);
            setFieldValue('garagingAddress.zip', '');
          }}
          onAddressLine1Change={value =>
            setFieldValue('garagingAddress.address1', value)
          }
          onAddressLine2Change={value =>
            setFieldValue('garagingAddress.address2', value)
          }
          onCityChange={value => setFieldValue('garagingAddress.city', value)}
          onStateChange={value => setFieldValue('garagingAddress.state', value)}
          onZipChange={value => setFieldValue('garagingAddress.zip', value)}
          errorMessages={{
            address1: touched.garagingAddress?.address1
              ? errors.garagingAddress?.address1
              : undefined,
            address2: touched.garagingAddress?.address2
              ? errors.garagingAddress?.address2
              : undefined,
            city: touched.garagingAddress?.city
              ? errors.garagingAddress?.city
              : undefined,
            state: touched.garagingAddress?.state
              ? errors.garagingAddress?.state
              : undefined,
            zip: touched.garagingAddress?.zip
              ? errors.garagingAddress?.zip
              : undefined,
          }}
          onBlur={handleBlur}
        />
      )}
      <Row className={styles.fixedHeight}>
        <Column>
          {vehicleOptionsQuestionsConfig.askForInstateParkingDuration &&
            env.vehicleNewQuestionsEnabled && (
              <FormInput
                variant="radioButtonGroup"
                label={`In a typical year, is the ${
                  vehicle
                    ? combineStrings([
                        vehicle.year,
                        vehicle.make,
                        vehicle.model,
                      ])
                    : 'vehicle'
                } parked in ${mailingAddress.state} for at least 10 months`}
                tooltip="If any driver is an active military member currently deployed (domestic or overseas) or on temporary duty assignment, select ‘Yes’."
                name="inStateParkingDuration"
                data-testid="in-state-parking-duration"
                options={inStateParkingDurations}
                value={values.inStateParkingDuration || ''}
                onChange={value => {
                  setFieldValue('inStateParkingDuration', value);
                }}
                errorMessage={
                  touched.inStateParkingDuration
                    ? errors.inStateParkingDuration
                    : undefined
                }
              />
            )}
        </Column>
        <Column>
          {vehicleOptionsQuestionsConfig.askForParkingType &&
            env.vehicleNewQuestionsEnabled && (
              <FormInput
                variant="selectAutocomplete"
                name="parkingType"
                data-testid="parking-type"
                autoComplete={'off'}
                onChange={value => {
                  setFieldValue('parkingType', value);
                }}
                onBlur={handleBlur}
                value={values.parkingType}
                errorMessage={
                  touched.parkingType ? errors.parkingType : undefined
                }
                label="When not in use, where is this vehicle parked?"
                options={parkingTypes}
              />
            )}
        </Column>
      </Row>
    </div>
  );
};
